// The offline device store's contract (BL-2340, ADR 066 — Offline Mode § 2 and § 6.1): what the
// native-device gateway's `openOfflineStore` returns. Types only, so every adapter shares them.
import type { AuctionListItem } from '@auctogon/core-admin/schemas/responses/auction';
import type { OfflineReferenceDto } from '@auctogon/core-admin/schemas/responses/offline-reference';
import type { SessionUserDto, UserTenantsPayload } from '@auctogon/core-admin/schemas/responses/session';
import type {
  OfflineAuctionDeltaDto,
  OfflineDeltaCursorDto,
  OfflineDeltaLotDto,
} from '@auctogon/core-admin/schemas/responses/offline-sync';

/**
 * Whose store this is. Every record, thumbnail and queue entry is keyed by both halves, and a store
 * opened for one scope never reads another's (ADR 066 § 6.1, limit 1).
 */
export interface OfflineScope {
  readonly tenantId: string;
  readonly userId: string;
}

/** One record for {@link OfflineRecords.putMany}. */
export interface OfflineRecordInput<C extends OfflineCollection> {
  key: string;
  value: OfflineRecordMap[C];
  /** A lot's auction id, so the auction's lots can be listed or dropped together. */
  parentKey?: string | null;
}

/** One thumbnail for {@link OfflineThumbnails.putMany}. */
export interface OfflineThumbnailInput {
  /** The thumbnail's key (the sync engine uses `<lotId>:<mediaId>`, BL-2429). */
  key: string;
  auctionId: string;
  blob: Blob;
}

/**
 * A scope that has not been used (opened, read or written) for this long is wiped on the next open of any scope
 * (ADR 066 § 6.1, limit 3, Michael Lawrie 2026-10-09).
 */
export const OFFLINE_STORE_IDLE_LIMIT_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * A stored row's key and size (BL-2429), read from the parent index without loading the row: a
 * record's UTF-8 JSON bytes, a thumbnail's bytes on the device (BL-2609).
 */
export interface OfflineEntrySize {
  key: string;
  bytes: number;
}

/** One synced auction: the delta's auction header and the cursor it was synced to (limit 2). */
export interface OfflineAuctionRecord {
  auction: OfflineAuctionDeltaDto['auction'];
  /**
   * The auction's row from `GET /api/auctions`, as the last completed sync read it, so the
   * offline Auctions list can answer from the store (BL-2343). Read only while offline.
   */
  listRow: AuctionListItem;
  cursor: OfflineDeltaCursorDto;
  /** When the delta that wrote this record was applied, ISO 8601. */
  syncedAt: string;
  /** The bytes the auction's records and thumbnails take on the device (BL-2344, BL-2429). */
  bytes: number;
  /**
   * The auction's thumbnails on the device, as one aggregate (BL-2609): how many, and the bytes
   * they take on disk. Kept here, not per photo, so a size is one record read. Absent on a record
   * written before BL-2609, whose thumbnails are then measured once from the store.
   */
  thumbnails?: OfflineThumbnailAggregate;
  /**
   * Lots whose thumbnails are still owed (BL-2429, BUG-2124): written when the auction's lot pages
   * finish (the lots they wrote), shrunk as the photo pass works through them, and left holding the
   * lots a stopped pass never reached and those whose download failed. The next pass starts here.
   * Absent on a record written before BL-2429, whose next pass checks every lot.
   */
  thumbnailRetryLotIds?: string[];
}

/** An auction's thumbnails on the device, counted (BL-2609). */
export interface OfflineThumbnailAggregate {
  count: number;
  /** What they take on disk. */
  bytes: number;
}

/**
 * This device's choice for one auction's Save offline switch (BL-2344, ADR 066 — Offline Mode
 * § 5). It is the device's own setting about what it caches, not a copy of server state. No
 * record means on: a newly eligible auction syncs until someone turns it off on this device.
 */
export interface OfflineAuctionSyncSetting {
  auctionId: string;
  enabled: boolean;
  /** When the switch was last flipped, ISO 8601. */
  changedAt: string;
}

/** The lot form's reference data and the strong ETag it was served with (BL-2356). */
export interface OfflineReferenceRecord {
  reference: OfflineReferenceDto;
  etag: string | null;
  /** When the read that wrote this record returned, ISO 8601. */
  syncedAt: string;
}

/**
 * The offline session (BL-2343, ADR 066 — Offline Mode § 2): the copy of `GET /api/auth/me` and
 * `GET /api/tenants/user` the app last stored online, so an offline cold start can mount the
 * dashboard chrome. One per scope, under {@link OFFLINE_SESSION_RECORD_KEY}.
 */
export interface OfflineSessionRecord {
  /** The signed-in user, as the session surfaces it. */
  user: SessionUserDto;
  /** The tenant lists the switcher renders. */
  tenants: UserTenantsPayload;
  /** When the session was stored, ISO 8601. */
  syncedAt: string;
}

/** The one key the `session` collection uses. */
export const OFFLINE_SESSION_RECORD_KEY = 'current';

/** The value each record collection holds, so a reader gets the DTO it stored back. */
export interface OfflineRecordMap {
  auctions: OfflineAuctionRecord;
  /** A lot's list row, detail and notes, as the delta delivers them. Parent key: its auction id. */
  lots: OfflineDeltaLotDto;
  reference: OfflineReferenceRecord;
  /** The Save offline switch per auction, keyed by auction id (BL-2344). */
  syncSettings: OfflineAuctionSyncSetting;
  session: OfflineSessionRecord;
}

/** A record collection's name. */
export type OfflineCollection = keyof OfflineRecordMap;

/** The cached server records of one scope. Read only while offline (ADR 066 § 6.1, limit 4). */
export interface OfflineRecords {
  /**
   * @param input - the collection and the record's key.
   * @returns the stored value, or null when there is none.
   */
  get<C extends OfflineCollection>(input: { collection: C; key: string }): Promise<OfflineRecordMap[C] | null>;
  /**
   * @param input - the collection and the records' keys, read together.
   * @returns each stored value in key order, null where there is none.
   */
  getMany<C extends OfflineCollection>(input: { collection: C; keys: string[] }): Promise<(OfflineRecordMap[C] | null)[]>;
  /**
   * Store a record, replacing any with the same key.
   *
   * @param input - the collection, key, value and optional parent key (a lot's auction id).
   * @returns once written.
   * @sideEffect Writes one record to the device store.
   */
  put<C extends OfflineCollection>(input: {
    collection: C;
    key: string;
    value: OfflineRecordMap[C];
    parentKey?: string | null;
  }): Promise<void>;
  /**
   * Store many records of one collection, a sync's worth at a time: written in batches, each one
   * transaction, rather than one flush per record. A key given twice keeps its last value.
   *
   * @param input - the collection and the records.
   * @returns once every batch is written.
   * @sideEffect Writes records to the device store.
   */
  putMany<C extends OfflineCollection>(input: { collection: C; items: OfflineRecordInput<C>[] }): Promise<void>;
  /**
   * @param input - the collection and the record's key.
   * @returns whether a record was removed.
   * @sideEffect Deletes one record from the device store.
   */
  delete(input: { collection: OfflineCollection; key: string }): Promise<boolean>;
  /**
   * @param input - the collection, and optionally the parent key to narrow to.
   * @returns every matching value, ordered by key.
   */
  list<C extends OfflineCollection>(input: { collection: C; parentKey?: string }): Promise<OfflineRecordMap[C][]>;
  /**
   * Remove every record under one parent, e.g. an auction's lots when its switch is turned off.
   *
   * @param input - the collection and the parent key.
   * @returns how many records were removed.
   * @sideEffect Deletes records from the device store.
   */
  deleteByParent(input: { collection: OfflineCollection; parentKey: string }): Promise<number>;
  /**
   * Remove many records of one collection in one batch, e.g. a delta page's tombstones (BL-2429).
   *
   * @param input - the collection and the keys; a key with no record is ignored.
   * @returns how many records were removed.
   * @sideEffect Deletes records from the device store.
   */
  deleteMany(input: { collection: OfflineCollection; keys: string[] }): Promise<number>;
  /**
   * The keys and sizes of every record under one parent, read from the parent index, so an
   * auction's size never loads its lots (BL-2429).
   *
   * @param input - the collection and the parent key.
   * @returns each record's key and UTF-8 JSON size, ordered by key.
   */
  entriesByParent(input: { collection: OfflineCollection; parentKey: string }): Promise<OfflineEntrySize[]>;
}

export type {
  OfflineThumbnailDownloadInput,
  OfflineThumbnailDownloadOutcome,
  OfflineThumbnails,
  OfflineThumbnailSource,
} from './thumbnail-types';
import type { LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import type { OfflineThumbnails } from './thumbnail-types';
import type { OfflineLotStates } from './lot-state-types';

export type { OfflineLotStates } from './lot-state-types';

/**
 * A change-queue entry's status (signed off by Michael Lawrie, 2026-10-09, rule #23). The store
 * records it and never moves it: the replay engine (BL-2345) owns every transition, including
 * resetting an entry a kill left `syncing`.
 * - `pending` — waiting to replay.
 * - `syncing` — its request is in flight.
 * - `failed` — the replay failed; Retry or Discard.
 * - `refused` — the server refused it, e.g. `STALE_FIELDS`; Discard or Overwrite.
 */
export type OutboxStatus = 'pending' | 'syncing' | 'failed' | 'refused';

/** A value that survives JSON and the structured clone unchanged. */
export type OutboxJson = string | number | boolean | null | OutboxJson[] | { [key: string]: OutboxJson };

/** Why an entry failed or was refused, as the replay recorded it. */
export interface OutboxError {
  message: string;
  code: string | null;
  httpStatus: number | null;
  /** The fields a `STALE_FIELDS` refusal named. Empty otherwise. */
  fieldNames: string[];
}

/** One queued operation: one future request to an online door (ADR 066 § 4). */
export interface OutboxEntry {
  /** Client-minted UUID. A create's entry reuses it as its `clientOpId` (BL-2337). */
  id: string;
  /** Replay order within the scope, assigned on append and never reused. */
  seq: number;
  /** Which door it replays through, e.g. `lots.byId.patch`. BL-2345 owns the vocabulary. */
  op: string;
  payload: OutboxJson;
  status: OutboxStatus;
  error: OutboxError | null;
  /** The entry this one waits on (a lot's photos wait on its create), or null. */
  dependsOn: string | null;
  /** Groups related entries for display and merging, e.g. `lot:<id>`, or null. */
  groupKey: string | null;
  attempts: number;
  createdAt: string;
  updatedAt: string;
}

/** What `outbox.append` needs: everything but the fields the store assigns. */
export interface OutboxAppendInput {
  id: string;
  op: string;
  payload: OutboxJson;
  dependsOn?: string | null;
  groupKey?: string | null;
}

/** A change to an entry's replay state. Omitted fields are left as they are. */
export interface OutboxUpdateInput {
  id: string;
  status?: OutboxStatus;
  error?: OutboxError | null;
  attempts?: number;
  /**
   * The entry's new payload (BL-2345): when a new lot syncs, the replay rewrites each entry waiting
   * on it to name the lot's server id, and an Overwrite marks the edit to skip the conflict check.
   */
  payload?: OutboxJson;
  /** The entry's new parent; null once the entry it waited on has synced (BL-2345). */
  dependsOn?: string | null;
}

/** Outcome of `outbox.append`. */
export type OutboxAppendResult =
  | { success: true; entry: OutboxEntry }
  | { success: false; code: 'DUPLICATE_ID' };

/**
 * One offline write (BL-2431): the queue entries a screen's save becomes, and the device's lot
 * records it changes so the offline screens show the change. Committed together or not at all.
 */
export interface OutboxLocalWriteInput {
  /** Appended in order, after every entry already queued. May be empty. */
  entries: OutboxAppendInput[];
  lots: {
    /** Lot records to write whole (an overlaid edit, or a lot created offline). */
    put: OfflineRecordInput<'lots'>[];
    /** Lot record keys to remove. */
    delete: string[];
  };
  /**
   * The lots' desired states this write changes (BL-2681), landing in the same batch. Omitted when
   * the write changes none.
   */
  lotStates?: {
    /** States to write whole. */
    put: LotDesiredState[];
    /** Lot ids whose state to remove. */
    delete: string[];
    /**
     * States to change as one read-modify-write inside the batch (a Save): `change` is given the
     * stored state, so a send settling meanwhile is read here, never overwritten by a stale copy.
     * Pure; returns the new state, or null to remove it.
     */
    update?: { lotId: string; change: (current: LotDesiredState | null) => LotDesiredState | null }[];
  };
  /**
   * Queued entries to remove in the same batch, by id; an id not queued is ignored. The one-time
   * move of an earlier build's lot entries into lot states uses it (BL-2681).
   */
  removeEntryIds?: string[];
}

/** Outcome of `outbox.appendLocalWrite`. */
export type OutboxLocalWriteResult =
  | { success: true; entries: OutboxEntry[] }
  | { success: false; code: 'DUPLICATE_ID' };

/** Outcome of `outbox.update`. */
export type OutboxUpdateResult = { success: true; entry: OutboxEntry } | { success: false; code: 'NOT_FOUND' };

/** The change queue of one scope, in replay order. */
export interface OfflineOutbox {
  /**
   * Add an entry at the end of the queue, `pending`, with no attempts.
   *
   * @param input - the entry's id, door, payload and optional parent and group.
   * @returns the stored entry, or `DUPLICATE_ID` when the id is already queued.
   * @sideEffect Writes one entry to the device store.
   */
  append(input: OutboxAppendInput): Promise<OutboxAppendResult>;
  /**
   * Queue one offline write's entries and update the lot records it changes, in one strict batch
   * (BL-2431), so an app killed mid-save never leaves half a save queued.
   *
   * @param input - the entries and the lot records to put and delete.
   * @returns the stored entries, or `DUPLICATE_ID` (nothing written) when an id is already queued.
   * @sideEffect Writes entries and lot records to the device store; resets the idle clock.
   */
  appendLocalWrite(input: OutboxLocalWriteInput): Promise<OutboxLocalWriteResult>;
  /** @returns every entry, in `seq` order. */
  list(): Promise<OutboxEntry[]>;
  /**
   * @param id - the entry's id.
   * @returns the entry, or null.
   */
  get(id: string): Promise<OutboxEntry | null>;
  /**
   * @param input - the entry's id and the replay state to record.
   * @returns the updated entry, or `NOT_FOUND`.
   * @sideEffect Rewrites one entry in the device store.
   */
  update(input: OutboxUpdateInput): Promise<OutboxUpdateResult>;
  /**
   * @param id - the entry's id.
   * @returns whether an entry was removed.
   * @sideEffect Deletes one entry from the device store.
   */
  remove(id: string): Promise<boolean>;
  /** @returns how many entries are queued. */
  count(): Promise<number>;
}

/**
 * The device's active tenant and user (BL-2343): the scope an offline cold start opens, before any
 * session exists to name one. Set on each signed-in session and tenant switch, cleared on
 * sign-out, which wipes nothing else (ADR 066 § 6.1 limit 3).
 */
export interface OfflineActiveScope {
  /**
   * @returns the active scope, or null when none is set (never signed in, or signed out).
   */
  read(): Promise<OfflineScope | null>;
  /**
   * @param scope - the scope to make active, or null to clear it.
   * @returns once written.
   * @sideEffect Writes the device-level pointer; touches no scope's data.
   */
  set(scope: OfflineScope | null): Promise<void>;
}

/** One scope's device store (ADR 066 § 2). */
export interface OfflineStore {
  readonly scope: OfflineScope;
  readonly records: OfflineRecords;
  readonly thumbnails: OfflineThumbnails;
  readonly outbox: OfflineOutbox;
  readonly lotStates: OfflineLotStates;
}

/** One staged file's metadata (BL-2421): what the replay needs to mint an upload URL for it. */
export interface StagedFile {
  /** The client-minted media or document id; also its file name on the device. */
  id: string;
  /** The queue entry that uploads it. A file whose entry is gone is swept on a later launch. */
  ownerEntryId: string;
  /** The name it is uploaded under, e.g. `walkaround.mp4`. */
  name: string;
  /** Its MIME type; `application/octet-stream` when the picked file had none. */
  mimeType: string;
  /** Its size in bytes. */
  size: number;
  /** When it finished staging, ISO 8601. */
  stagedAt: string;
}

/** What {@link OfflineMediaStaging.stage} needs. */
export interface StageFileInput {
  /** A safe path segment (`A–Z a–z 0–9 _ -`, at most 200 characters). */
  id: string;
  ownerEntryId: string;
  name: string;
  blob: Blob;
}

/** Outcome of {@link OfflineMediaStaging.stage}. */
export type StageFileResult =
  | { success: true; file: StagedFile }
  | { success: false; code: 'DUPLICATE_ID' | 'INVALID_ID' };

/**
 * One scope's staged photos, videos and documents (BL-2421, ADR 066 — Offline Mode § 2). Files
 * cross the native bridge a few megabytes at a time, so a large video is never one string.
 */
export interface OfflineMediaStaging {
  readonly scope: OfflineScope;
  /**
   * Copy a file into the scope's staging folder, in slices.
   *
   * @param input - the file's id, owning queue entry, upload name and bytes.
   * @returns the staged file, or `DUPLICATE_ID` / `INVALID_ID` with nothing written.
   * @throws when reading the blob or writing the device fails; the partial file is removed.
   * @sideEffect Writes the file and its metadata to the device.
   */
  stage(input: StageFileInput): Promise<StageFileResult>;
  /**
   * @param id - the file's id.
   * @returns the staged file's metadata, or null (also while it is still being written).
   */
  get(id: string): Promise<StagedFile | null>;
  /** @returns every fully staged file, ordered by id. */
  list(): Promise<StagedFile[]>;
  /**
   * Read a staged file back, in slices, for upload.
   *
   * @param id - the file's id.
   * @returns its metadata and bytes (the blob carries its MIME type), or null.
   * @throws when the file on the device is missing or shorter than recorded.
   */
  read(id: string): Promise<{ file: StagedFile; blob: Blob } | null>;
  /**
   * @param id - the file's id.
   * @returns whether a file was removed.
   * @sideEffect Deletes the file and its metadata from the device.
   */
  delete(id: string): Promise<boolean>;
}
